/**
 * PATH: apps/hr/src/pages/employees/employee-detail/SupportTab.tsx
 * PURPOSE: Support tickets tab + placeholder tabs for leave/payroll
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Ticket, CalendarOff, DollarSign } from 'lucide-react'
import { cn, formatDate, formatStatus } from '../../../lib/utils'
import { hrApi } from '../../../lib/setupApi'
import { CreateRequestModal } from '../../../components/support/CreateRequestModal'
import type { LeaveRequest, PayslipLine } from '../../../types'

// =============================================================================
// LEAVE HISTORY TAB (placeholder)
// =============================================================================

export function LeaveHistoryTab({ leaveRequests: _ }: { leaveRequests: LeaveRequest[] }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 py-16 text-center">
      <CalendarOff className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Leave History</h3>
      <p className="text-slate-500 dark:text-slate-400 text-sm">Coming soon - API integration pending</p>
    </div>
  )
}

// =============================================================================
// PAYROLL HISTORY TAB (placeholder)
// =============================================================================

export function PayrollHistoryTab({ payslips: _ }: { payslips: PayslipLine[] }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 py-16 text-center">
      <DollarSign className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Payroll History</h3>
      <p className="text-slate-500 dark:text-slate-400 text-sm">Coming soon - API integration pending</p>
    </div>
  )
}

// =============================================================================
// SUPPORT TAB
// =============================================================================

interface ApiSupportTicket {
  id: string; title: string; description: string; ticket_type: string
  priority: string; status: string; created_at: string; due_date?: string
  linked_employee?: { first_name: string; last_name: string }
}

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  pending: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
}
const PRIORITY_COLORS: Record<string, string> = { low: 'text-slate-500', medium: 'text-blue-600', high: 'text-amber-600', urgent: 'text-red-600' }

export default function SupportTab({ employeeId, employeeName }: { employeeId: string; employeeName: string }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ['employee-support-tickets', employeeId],
    queryFn: () => hrApi.getEmployeeSupportTickets(employeeId),
    enabled: !!employeeId,
  })
  const tickets: ApiSupportTicket[] = ticketsData?.data?.items || []

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 text-teal-600 animate-spin" /></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-600 dark:text-slate-400">{tickets.length} {tickets.length === 1 ? 'ticket' : 'tickets'}</h3>
        <button onClick={() => setIsCreateModalOpen(true)} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"><Ticket className="w-4 h-4" />New Request</button>
      </div>

      {tickets.length > 0 ? (
        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div key={ticket.id} onClick={() => navigate(`/support/${ticket.id}`)} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{ticket.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{ticket.description}</p>
                </div>
                <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium ml-3 shrink-0', STATUS_COLORS[ticket.status] || STATUS_COLORS.open)}>{formatStatus(ticket.status)}</span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs">
                <span className={cn('font-medium', PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.medium)}>{ticket.priority?.toUpperCase() || 'MEDIUM'}</span>
                <span className="text-slate-400">{formatDate(ticket.created_at)}</span>
                {ticket.due_date && <span className="text-slate-400">Due: {formatDate(ticket.due_date)}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Ticket className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mb-4">No support requests for this employee</p>
          <button onClick={() => setIsCreateModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"><Ticket className="w-4 h-4" />Create New Request</button>
        </div>
      )}

      <CreateRequestModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} preselectedEmployee={{ id: employeeId, name: employeeName }}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['employee-support-tickets', employeeId] })} />
    </div>
  )
}
